import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LocationKiosk = ({ location, onClose }) => {
  const [currentStep, setCurrentStep] = useState('action'); // action, initial, name, pin, confirmation
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedInitial, setSelectedInitial] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [pin, setPin] = useState('');
  const [availableInitials, setAvailableInitials] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Reset kiosk to initial state
  const resetKiosk = () => {
    setCurrentStep('action');
    setSelectedAction('');
    setSelectedInitial('');
    setSelectedEmployee(null);
    setPin('');
    setEmployees([]);
  };

  // Fetch available initials for this location
  const fetchInitials = async () => {
    try {
      const response = await axios.get(`${API}/employees/initials/${location.id}`);
      setAvailableInitials(response.data.initials);
    } catch (error) {
      console.error('Error fetching initials:', error);
      toast.error('Error loading employee initials');
    }
  };

  // Fetch employees by initial for this location
  const fetchEmployeesByInitial = async (initial) => {
    try {
      const response = await axios.get(`${API}/employees/by-initial/${location.id}/${initial}`);
      setEmployees(response.data.employees);
      
      // If no employees found for this initial, show message
      if (response.data.employees.length === 0) {
        toast.info(`No employees found with initial "${initial}" at ${location.name}`);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      
      // Show empty list and inform user
      setEmployees([]);
      toast.error(`Cannot load employees for initial "${initial}". Please contact IT support.`);
    }
  };

  // Handle kiosk submission - SECURE: No UI changes until PIN is verified
  const handleSubmit = async () => {
    if (!selectedEmployee || !pin) return;

    // Validate PIN format before sending
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/kiosk/submit/${location.id}`, {
        username: selectedEmployee.username,
        pin: pin.trim(), // Ensure no whitespace
        action: selectedAction
      });

      // Only show success and reset if PIN was verified and event was created
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Auto-reset after 2 seconds only on successful PIN verification
        setTimeout(() => {
          resetKiosk();
        }, 2000);
      } else {
        toast.error('Attendance recording failed');
      }

    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      
      // Handle different error types
      if (error.response?.status === 401) {
        toast.error('Incorrect PIN - Please try again');
        // Clear PIN field on incorrect PIN
        setPin('');
      } else if (error.response?.status === 423) {
        toast.error('Account temporarily locked due to too many incorrect attempts');
        // Reset to employee selection on lockout
        setCurrentStep('name');
        setPin('');
      } else if (error.response?.status === 400) {
        toast.error(message); // State machine errors (already checked in/out)
      } else if (error.response?.status === 429) {
        toast.error('Please wait before trying again');
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle action selection
  const handleActionSelect = (action) => {
    setSelectedAction(action);
    setCurrentStep('initial');
    fetchInitials();
  };

  // Handle initial selection
  const handleInitialSelect = (initial) => {
    setSelectedInitial(initial);
    setCurrentStep('name');
    fetchEmployeesByInitial(initial);
  };

  // Handle employee selection
  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setCurrentStep('pin');
  };

  // Handle PIN input - SECURE: Only allow 4 digits, no special characters
  const handlePinChange = (value) => {
    // Strip any non-digit characters and limit to 4 characters
    const cleanValue = value.replace(/\D/g, '').slice(0, 4);
    setPin(cleanValue);
  };

  // Handle PIN submission
  const handlePinSubmit = () => {
    if (pin.length === 4) {
      handleSubmit();
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'action':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-slate-700 mb-2">
                {location.name}
              </h2>
              <p className="text-slate-600 mb-6">Choose your action</p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Button
                size="lg"
                className="h-24 text-xl font-medium bg-slate-400 hover:bg-slate-500 text-white shadow-lg"
                onClick={() => handleActionSelect('check_in')}
              >
                Check In
              </Button>
              <Button
                size="lg"
                className="h-24 text-xl font-medium bg-slate-400 hover:bg-slate-500 text-white shadow-lg"
                onClick={() => handleActionSelect('check_out')}
              >
                Check Out
              </Button>
            </div>
          </div>
        );

      case 'initial':
        return (
          <div className="space-y-8 relative">
            <button
              onClick={resetKiosk}
              className="absolute top-0 left-0 p-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="text-center">
              <Badge className="mb-4 bg-slate-100 text-slate-700">
                {location.name} - {selectedAction === 'check_in' ? 'Check In' : 'Check Out'}
              </Badge>
              <h2 className="text-2xl font-semibold text-slate-700">
                First name initial
              </h2>
            </div>
            <div className="flex justify-center">
              <div className="grid grid-cols-6 gap-3 max-w-md">
                {availableInitials.map((initial) => (
                  <Button
                    key={initial}
                    variant="outline"
                    className="h-16 text-lg font-medium border-2 hover:bg-slate-50"
                    onClick={() => handleInitialSelect(initial)}
                  >
                    {initial}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'name':
        return (
          <div className="space-y-8 relative">
            <button
              onClick={() => setCurrentStep('initial')}
              className="absolute top-0 left-0 p-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="text-center">
              <Badge className="mb-4 bg-slate-100 text-slate-700">
                {location.name} - {selectedAction === 'check_in' ? 'Check In' : 'Check Out'} → {selectedInitial}
              </Badge>
              <h2 className="text-2xl font-semibold text-slate-700">
                Select your name
              </h2>
            </div>
            <div className="space-y-3">
              {employees.map((employee) => (
                <Button
                  key={employee.id}
                  variant="outline"
                  className="w-full h-16 text-left border-2 hover:bg-slate-50"
                  onClick={() => handleEmployeeSelect(employee)}
                >
                  <div>
                    <div className="text-lg font-medium">
                      {employee.first_name} {employee.last_name}
                    </div>
                    <div className="text-sm text-slate-500">
                      {employee.username}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        );

      case 'pin':
        return (
          <div className="space-y-8 relative">
            <button
              onClick={() => setCurrentStep('name')}
              className="absolute top-0 left-0 p-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="text-center">
              <Badge className="mb-4 bg-slate-100 text-slate-700">
                {location.name} - {selectedEmployee?.first_name} {selectedEmployee?.last_name}
              </Badge>
              <h2 className="text-2xl font-semibold text-slate-700">
                Enter your 4-digit PIN
              </h2>
            </div>
            <div className="flex justify-center">
              <Input
                type="password"
                placeholder="••••"
                value={pin}
                onChange={(e) => handlePinChange(e.target.value)}
                className="w-32 h-16 text-center text-2xl font-mono border-2"
                maxLength={4}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handlePinSubmit();
                  }
                }}
              />
            </div>
            <div className="text-center">
              <Button
                size="lg"
                className="w-40 h-12 bg-slate-700 hover:bg-slate-800 text-white"
                onClick={handlePinSubmit}
                disabled={pin.length !== 4 || isLoading}
              >
                {isLoading ? 'Processing...' : 'Submit'}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
      <Card className="w-full max-w-2xl shadow-xl border-0 bg-white rounded-lg relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors z-10"
        >
          <X size={24} />
        </button>
        
        <CardContent className="p-12">
          {renderCurrentStep()}
        </CardContent>
      </Card>
    </div>
  );
};

export default LocationKiosk;